import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Box,
  CircularProgress,
  Collapse,
  Badge,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { useObservationsModal } from "../../../../context/ObservationsModalContext/ObservationsModalContext";
import { ObservationContext } from "../../../../context/ObservationContext/ObservationContext";
import { formatDateTime } from "../../../../utils/formatters/date";
import useAuthUser from "../../../../hooks/useAuthUser";
import {
  OBSERVATION_STATUS,
  OBSERVATION_STATUS_OPTIONS,
  OBSERVATION_STATUS_COLORS,
} from "../../../../utils/services/Observations/observationConstants";

export default function ObservationsModal() {
  const {
    open,
    cipId,
    componentId,
    patientName,
    closeObservationsModal,
  } = useObservationsModal();

  const {
    observations,
    loading,
    error,
    setCipId,
    addObservation,
    changeObservationStatus,
  } = useContext(ObservationContext);

  const user = useAuthUser();
  const employeeId = user?.employeeId || user?.employee || null;

  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(OBSERVATION_STATUS.OPEN);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (open && cipId) {
      setCipId(cipId);
    }
  }, [open, cipId, setCipId]);

  useEffect(() => {
    if (!open) {
      setMessage("");
      setStatus(OBSERVATION_STATUS.OPEN);
      setSaving(false);
      setShowForm(false);
    }
  }, [open]);

  const handleAdd = async () => {
    if (!message.trim() || !cipId) return;

    setSaving(true);
    const payload = {
      cipId: String(cipId),
      message: message.trim(),
      componentId: 1,
      priority: null,
      employeeId,
      status,
    };
    try {
      await addObservation(payload);
      setMessage("");
      setStatus(OBSERVATION_STATUS.OPEN);
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSoftDelete = async (observationId) => {
    await changeObservationStatus({
      observationId,
      newStatus: OBSERVATION_STATUS.DELETED,
      employeeId,
    });
  };

  const title = patientName
    ? `${patientName}`
    : cipId
    ? `CIP ${cipId}`
    : "Patient";

  const obsCount = observations?.length || 0;

  return (
    <Dialog
      open={open}
      onClose={closeObservationsModal}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: "80vh",
          maxHeight: 700,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2.5,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: "#EDE9FE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NoteAltOutlinedIcon sx={{ color: "#7C3AED", fontSize: 20 }} />
          </Box>
          <Stack spacing={-0.3}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "1rem", lineHeight: 1.3 }}>
              Observations
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {title}
              {obsCount > 0 && ` \u2022 ${obsCount} observation${obsCount !== 1 ? "s" : ""}`}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button
            variant={showForm ? "outlined" : "contained"}
            size="small"
            startIcon={showForm ? <KeyboardArrowUpIcon /> : <AddIcon />}
            onClick={() => setShowForm(!showForm)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              fontSize: "0.8rem",
              ...(showForm
                ? {
                    borderColor: "#8B5CF6",
                    color: "#7C3AED",
                    "&:hover": { bgcolor: "rgba(139, 92, 246, 0.08)", borderColor: "#7C3AED" },
                  }
                : {
                    bgcolor: "#8B5CF6",
                    "&:hover": { bgcolor: "#7C3AED" },
                  }),
            }}
          >
            {showForm ? "Hide form" : "New observation"}
          </Button>
          <IconButton size="small" onClick={closeObservationsModal}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        {/* Formulario colapsable */}
        <Collapse in={showForm}>
          <Box sx={{ px: 2.5, py: 2, bgcolor: "#FAFAFE", borderBottom: "1px solid #E5E7EB" }}>
            <Stack spacing={1.5}>
              <TextField
                label="Write your observation"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                minRows={2}
                maxRows={4}
                fullWidth
                size="small"
                placeholder="Type observation here..."
                autoFocus
              />
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    label="Status"
                  >
                    {OBSERVATION_STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setShowForm(false);
                    setMessage("");
                  }}
                  sx={{ textTransform: "none", borderRadius: 2, color: "text.secondary", borderColor: "#D1D5DB" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAdd}
                  disabled={!message.trim() || !cipId || saving}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    bgcolor: "#8B5CF6",
                    "&:hover": { bgcolor: "#7C3AED" },
                  }}
                >
                  {saving ? "Saving..." : "Save observation"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Collapse>

        {/* Lista de observaciones */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {loading ? (
            <Stack alignItems="center" py={6}>
              <CircularProgress size={32} sx={{ color: "#8B5CF6" }} />
              <Typography variant="body2" color="text.secondary" mt={1.5}>
                Loading observations...
              </Typography>
            </Stack>
          ) : error ? (
            <Stack alignItems="center" py={6}>
              <Typography variant="body2" color="error">
                Error loading observations
              </Typography>
            </Stack>
          ) : !observations || observations.length === 0 ? (
            <Stack alignItems="center" py={6} spacing={1}>
              <NoteAltOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB" }} />
              <Typography variant="body2" color="text.secondary">
                No observations for this patient yet.
              </Typography>
              {!showForm && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowForm(true)}
                  sx={{ textTransform: "none", color: "#8B5CF6", mt: 1 }}
                >
                  Add the first observation
                </Button>
              )}
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {observations.map((obs) => (
                <Box
                  key={obs.observationId}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#FAFAFA",
                    border: "1px solid #F0F0F0",
                    transition: "all 0.15s",
                    "&:hover": {
                      bgcolor: "#F5F3FF",
                      borderColor: "#E9E5FF",
                    },
                  }}
                >
                  {/* Header */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: "#EDE9FE",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          color: "#7C3AED",
                        }}
                      >
                        {(obs.employeeName || "U")[0].toUpperCase()}
                      </Box>
                      <Stack spacing={-0.3}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem" }}>
                          {obs.employeeName || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                          {formatDateTime(obs.createdAt, "")}
                        </Typography>
                      </Stack>
                    </Stack>
                    {obs.status !== OBSERVATION_STATUS.DELETED && (
                      <Tooltip title="Delete observation" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleSoftDelete(obs.observationId)}
                          sx={{
                            color: "#9CA3AF",
                            "&:hover": { color: "#EF4444", bgcolor: "#FEF2F2" },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  {/* Mensaje */}
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      whiteSpace: "pre-wrap",
                      pl: 4.75,
                      fontSize: "0.85rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {obs.message}
                  </Typography>

                  {/* Chips */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ pl: 4.75 }}>
                    <Chip
                      label={obs.statusName || "Unknown"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.63rem",
                        fontWeight: 600,
                        color: OBSERVATION_STATUS_COLORS[obs.status] || "gray",
                        bgcolor: "transparent",
                        border: `1px solid ${OBSERVATION_STATUS_COLORS[obs.status] || "gray"}`,
                      }}
                    />
                    {obs.componentName && (
                      <Chip
                        label={obs.componentName}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.63rem",
                          color: "#6B7280",
                          bgcolor: "#F3F4F6",
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
